import axios from "axios";

/**
 * Per-tenant branding (title, favicon, logos, terms text) served by
 * GET /get-initial-data and cached in localStorage under `webData`.
 *
 * The tab used to sit on "Loading" until you refreshed, because:
 *   - index.html hardcodes <title>Loading</title>
 *   - App.jsx read webData once at *module scope*, so on a first visit it was
 *     always {} (nothing cached yet)
 *   - its useEffect had [] deps, so when the login page later fetched and cached
 *     the branding, nothing re-applied the title
 *   - `webData ? webData.title ? ... : 'loading' : 'Loading'` — {} is truthy, so
 *     it always landed on the 'loading' branch
 *
 * Fixed by making this the one place branding is fetched and applied, applying
 * it synchronously from cache at boot (see the inline script in index.html), and
 * re-applying whenever it changes.
 */

const STORAGE_KEY = "webData";

/** Fired after webData changes so anything showing branding can re-read it. */
export const BRANDING_UPDATED = "branding:updated";

export const getCachedWebData = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    // A corrupt cache must not take the whole app down.
    return null;
  }
};

/**
 * The API base must end in `/api`; strip it to build asset URLs. Same trick the
 * rest of the app uses, kept in one place here.
 */
const assetBase = () => (process.env.REACT_APP_API_BASE_URL || "").slice(0, -4);

export const assetUrl = (path) => (path ? assetBase() + path : null);

/** Push title + favicon into the document. Safe to call repeatedly. */
export const applyBranding = (webData) => {
  if (!webData) return;

  if (webData.title) {
    document.title = webData.title;
  }

  const icon = assetUrl(webData.email_logo);
  if (icon) {
    let link = document.querySelector("[rel=icon]");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    // Guarded: the old code did `slice(0,-4) + webData.email_logo` with no
    // check, so a missing logo produced a favicon URL ending in "undefined".
    link.href = icon;
  }
};

export const cacheWebData = (webData) => {
  if (!webData) return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(webData));
  applyBranding(webData);
  window.dispatchEvent(new CustomEvent(BRANDING_UPDATED, { detail: webData }));
};

// Shared so that App and the auth pages mounting together on a first visit make
// one request between them, not four.
let inFlight = null;

export const fetchWebData = () => {
  if (inFlight) return inFlight;

  inFlight = axios
    .get(process.env.REACT_APP_API_BASE_URL + "/get-initial-data")
    .then((res) => {
      const webData = res.data?.data;
      cacheWebData(webData);
      return webData;
    })
    .catch(() => null)
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/**
 * Apply cached branding immediately, then refresh from the API.
 *
 * Always refetches rather than only when the cache is empty — otherwise a tenant
 * that renames itself or changes its logo would never update for anyone who had
 * already visited.
 */
export const loadBranding = () => {
  const cached = getCachedWebData();
  applyBranding(cached);
  return fetchWebData();
};
